import axios from "axios";
import TokenService from "./token";
import { APIS } from "../endpoints";
import {
  markLoggedOut,
  isLoggedOut,
  resetLoggedOutGuard,
} from "../util/sessionGuard";
import { withRefreshLock } from "../util/refreshLock";

const instance = axios.create({
  baseURL: APIS.BASE_URL,
  debug: true,
  headers: {
    "content-type": "application/json",
  },
});

const instance_ref = axios.create({
  baseURL: APIS.BASE_URL,
  debug: true,
  headers: {
    "content-type": "application/json",
  },
});

instance.interceptors.request.use(
  async function (config) {
    // Do something before request is sent

    if (config.url === APIS.login || config.url === APIS.request_otp)
      return config;
    const token = await TokenService.getLocalAccessToken();
    const refToken = await TokenService.getLocalRefreshToken();
    if (token) {
      config.headers.Authorization = "Bearer " + token; // for Spring Boot back-end
      config.headers["x-access-token"] = token; // for Node.js Express back-end
    }
    if (config.url === APIS.refresh_token) {
      config.headers.Authorization = "Bearer " + refToken; // for Spring Boot back-end
      config.headers["x-access-token"] = refToken;
    }
    return config;
  },
  function (error) {
    // Do something with request error
    console.log("request error", error);
    throw new Error(error);
  },
);

// De-dupes concurrent refresh attempts within this tab: if several requests
// 401 around the same time, they all await this single in-flight refresh
// call instead of each firing their own POST /admin/refreshToken.
//
// withRefreshLock extends this across tabs (see util/refreshLock.js). The
// actual network call AND the resulting token storage write both happen
// inside the locked section, so a tab that was waiting for the lock reads
// the just-rotated token once it acquires it, rather than racing the tab
// that got there first with the (now stale) token it started with.
let refreshPromise = null;

function refreshAccessToken() {
  if (!refreshPromise) {
    refreshPromise = withRefreshLock(async () => {
      const ref_token = await TokenService.getLocalRefreshToken();
      const rs = await instance_ref.post(APIS.refresh_token, {
        token: ref_token,
      });
      const tokenData = rs.data.data;
      await TokenService.updateLocalAccessToken(tokenData);
      return tokenData;
    }).finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

// Centralized cleanup for every forced-logout path that doesn't go through a
// React component (refresh token expired/rotated-out/revoked, or a
// persistent 401 after a "successful" refresh). Manual logout uses the same
// localStorage.clear() via useLogout.js, which also sets this guard.
function forceLogout() {
  markLoggedOut();
  TokenService.removeUser();
  localStorage.clear();
  window.location.href = "/admin-login";
}

instance.interceptors.response.use(
  async (response) => {
    // Any status code that lie within the range of 2xx cause this function to trigger
    // Do something with response data
    //Successful Login Store token
    if (response.config.url === APIS.login && response.data) {
      resetLoggedOutGuard();
      await TokenService.updateLocalAccessToken(response.data);
    }

    return response;
  },
  async (err) => {
    // Any status codes that falls outside the range of 2xx cause this function to trigger
    // Do something with response error
    const originalConfig = err.config;
    //Need to navigate to login screen

    // On Successful Login store acces tokens in storage
    if (originalConfig.url !== APIS.login && err.response) {
      // Access Token was expired
      if (err.response.status === 401) {
        // A logout (manual, or another forced logout) already started in
        // this tab — don't let an in-flight refresh resurrect it.
        if (isLoggedOut()) {
          return Promise.reject(err);
        }

        if (!originalConfig._retry) {
          originalConfig._retry = true;

          try {
            // Token storage is already written inside refreshAccessToken
            // (see its comment) — before the cross-tab lock releases, so a
            // tab that had to wait picks up the freshly-rotated token.
            await refreshAccessToken();
            if (isLoggedOut()) {
              return Promise.reject(err);
            }
            return instance(originalConfig);
          } catch (_error) {
            console.log("ref token expired", _error);
            _error.response && (_error.response.logout = true);
            forceLogout();
            return Promise.reject(_error);
          }
        }

        // Already retried once after a refresh and still got a 401 — the new
        // access token is being rejected too, so this session is unrecoverable.
        forceLogout();
      }
    }

    console.log("response error", err.response);
    // return Promise.reject(err);
    throw err;
  },
);
/*instance.interceptors.response.use(
  res => {
    console.log("response res", res);
    return res;
  },
  async err => {
    console.log("response error ***", err);
    const originalConfig = err.config;

    if (originalConfig.url !== APIS.request_otp && err.response) {
      // Access Token was expired
      if (err.response.status === 401 && !originalConfig._retry) {
        originalConfig._retry = true;

        try {
          const rs = await instance.post(APIS.refresh_token, {
            refreshToken: TokenService.getLocalRefreshToken(),
          });

          const {accessToken} = rs.data;
          TokenService.updateLocalAccessToken(accessToken);

          return instance(originalConfig);
        } catch (_error) {
          return Promise.reject(_error);
        }
      }
    }

    return Promise.reject(err);
  },
);
*/

export default instance;
